import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ExternalLink, Info } from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import { peecAPI } from '../api/peecAPI';
import { Badge, DomainIdentity, FilterBar, FilterConfig, MiniTabs, PageHeader, PageShell, SearchToolbar, SectionCard, TableFrame, Td, Th, ToolButton, ToolbarIcons } from '../components/PeecUI';
import { HighlightBarChart } from '../components/charts/HighlightBarChart';
import { DomainTypesChart } from '../components/charts/DomainTypesChart';
import { DomainTypeShare, SourceDomainRow, SourceUrlRow } from '../types';

type PageTab = 'Domains' | 'Hosts' | 'URLs';

interface DomainsPageProps {
  workspaceId: string;
  initialTab?: PageTab;
}

const PAGE_TABS: PageTab[] = ['Domains', 'Hosts', 'URLs'];

const DomainsPage: React.FC<DomainsPageProps> = ({ workspaceId, initialTab }) => {
  const { t } = useI18n();
  const [domains, setDomains] = useState<SourceDomainRow[]>([]);
  const [types, setTypes] = useState<DomainTypeShare[]>([]);
  const [urls, setUrls] = useState<SourceUrlRow[]>([]);
  const [domainMovers, setDomainMovers] = useState<Array<{ domain: string; retrievals: number }>>([]);
  const [tableTabIdx, setTableTabIdx] = useState<number>(initialTab ? PAGE_TABS.indexOf(initialTab) : 0);
  const [moverTabIdx, setMoverTabIdx] = useState<number>(3); // default: Losing
  const [timeRange, setTimeRange] = useState('全部时间');
  const [domainTypeFilter, setDomainTypeFilter] = useState('全部类型');

  const tableTab = PAGE_TABS[tableTabIdx];
  const pageTabLabels = [t('pages.domain'), t('pages.hosts'), t('pages.urls')];
  const moverTabLabels = ['热门', '新增', '上升', '下降'];

  useEffect(() => {
    peecAPI.getDomains(workspaceId).then((data) => {
      setDomains(data?.data ?? []);
      setTypes(data?.domain_types ?? []);
    }).catch((error) => console.error('Failed to fetch domains:', error));
    peecAPI.getUrls(workspaceId).then((data) => setUrls(data?.data ?? [])).catch((error) => console.error('Failed to fetch URLs:', error));
    peecAPI.getDomainMovers(workspaceId).then((data) => setDomainMovers(data ?? [])).catch((error) => console.error('Failed to fetch domain movers:', error));
  }, [workspaceId]);

  const moverChartItems = useMemo(() => domainMovers.map((domain) => ({
    label: domain.domain,
    value: domain.retrievals,
    iconUrl: `https://www.google.com/s2/favicons?domain=${domain.domain}&sz=64`,
    valueLabel: `${domain.retrievals} –`,
  })), [domainMovers]);

  const urlMoverChartItems = useMemo(() => urls.map((url) => ({
    label: url.title,
    value: url.retrieved,
    iconUrl: `https://www.google.com/s2/favicons?domain=${url.domain}&sz=64`,
    valueLabel: `${url.retrieved.toFixed(1)}% –`,
  })), [urls]);

  const filteredDomains = domainTypeFilter === '全部类型'
    ? domains
    : domains.filter((d) => d.domain_type === domainTypeFilter);

  const filteredUrls = domainTypeFilter === '全部类型'
    ? urls
    : urls.filter((u) => u.domain_type === domainTypeFilter);

  const domainFilters: FilterConfig[] = [
    { key: 'time', label: '全部时间', options: ['全部时间', '过去7天', '过去30天', '过去90天'], value: timeRange, onChange: setTimeRange },
    { key: 'type', label: '全部类型', options: ['全部类型', 'Corporate', 'UGC', 'Institutional', 'Reference'], value: domainTypeFilter, onChange: setDomainTypeFilter },
  ];

  const isUrls = tableTab === 'URLs';
  const domainMoverTitle = isUrls ? t('pages.urlMovers') : t('pages.domainMovers');
  const domainTypeTitle = isUrls ? t('pages.urlTypesTitle') : t('pages.domainTypes');

  return (
    <PageShell>
      <PageHeader title={t('pages.allDomains')} eyebrow={t('sidebar.dashboard')} />
      <FilterBar filters={domainFilters} />
      <MiniTabs items={pageTabLabels} active={pageTabLabels[tableTabIdx]} onChange={(v) => setTableTabIdx(pageTabLabels.indexOf(v))} />

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
        <SectionCard className="flex min-h-[360px] flex-col" title={domainMoverTitle} subtitle={t('pages.howOftenAppears')}>
          <div className="flex items-center justify-between border-b border-black/[0.05] px-5 py-3">
            <MiniTabs items={moverTabLabels} active={moverTabLabels[moverTabIdx]} onChange={(item) => setMoverTabIdx(moverTabLabels.indexOf(item))} />
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-400" title={t('pages.howOftenAppears')}>
              <Info size={12} aria-hidden="true" />
            </span>
          </div>
          <div className="px-4 py-3">
            <HighlightBarChart
              items={isUrls ? urlMoverChartItems : moverChartItems}
              barColor="bg-zinc-400/50"
              valueDecimals={isUrls ? 1 : 0}
            />
          </div>
        </SectionCard>

        <SectionCard
          className="flex min-h-[360px] flex-col"
          title={domainTypeTitle}
          action={(
            <span className="inline-flex items-center gap-1 text-xs font-medium text-zinc-400">
              <Info size={12} aria-hidden="true" />
              <span>{t('pages.totalRetrievals')}: 3.9k</span>
            </span>
          )}
        >
          <div className="px-4 py-3">
            <DomainTypesChart data={types} />
          </div>
        </SectionCard>
      </div>

      {isUrls ? (
        <SectionCard className="mt-5" title={t('pages.urlsPage')} subtitle={t('pages.everyPageAIUsed')}>
          <SearchToolbar>
            <ToolButton><span>{t('pages.allURLTypes')}</span><ChevronDown size={14} /></ToolButton>
            <ToolbarIcons />
          </SearchToolbar>
          <TableFrame>
            <thead><tr><Th>URL</Th><Th>{t('pages.domainType')}</Th><Th>{t('pages.retrieved')}</Th><Th>{t('pages.retrievalRate')}</Th><Th>{t('pages.citationRate')}</Th><Th>{t('pages.mentions')}</Th><Th>{t('pages.open')}</Th></tr></thead>
            <tbody>
              {filteredUrls.map((url) => (
                <tr key={url.id} className="hover:bg-zinc-50">
                  <Td><div className="max-w-lg"><div className="font-medium text-zinc-950">{url.title}</div><div className="truncate text-xs text-zinc-500">{url.url}</div></div></Td>
                  <Td><Badge tone={url.domain_type === 'Corporate' ? 'green' : url.domain_type === 'UGC' ? 'purple' : 'slate'}>{url.domain_type}</Badge></Td>
                  <Td className="font-semibold">{url.retrieved.toFixed(1)}%</Td>
                  <Td>{url.retrieval_rate.toFixed(1)}</Td>
                  <Td>{url.citation_rate.toFixed(1)}</Td>
                  <Td>{url.mentions}</Td>
                  <Td><a className="text-zinc-400 hover:text-zinc-900" href={url.url} target="_blank" rel="noreferrer"><ExternalLink size={16} /></a></Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
          <div className="px-4 py-3 text-sm text-zinc-500">{filteredUrls.length} {t('pages.ofItems')} 1,243</div>
        </SectionCard>
      ) : (
        <SectionCard className="mt-5" title={t('pages.domain')} subtitle={t('pages.domainsDetailed')} action={<label className="flex items-center gap-2 text-sm text-zinc-600"><input type="checkbox" className="h-4 w-4" /> {t('pages.gapAnalysisTitle')}</label>}>
          <SearchToolbar>
            <ToolButton><span>{t('pages.allDomainTypes')}</span><ChevronDown size={14} /></ToolButton>
            <ToolbarIcons />
          </SearchToolbar>
          <TableFrame>
            <thead><tr><Th>{t('pages.source')}</Th><Th>{t('pages.domainType')}</Th><Th>{t('pages.retrievals')}</Th><Th>{t('pages.retrieved')}</Th><Th>{t('pages.retrievalRate')}</Th><Th>{t('pages.citationRate')}</Th></tr></thead>
            <tbody>
              {filteredDomains.map((domain) => (
                <tr key={domain.id} className="hover:bg-zinc-50">
                  <Td><DomainIdentity domain={domain.domain} rank={domain.rank} /></Td>
                  <Td><Badge tone={domain.domain_type === 'Corporate' ? 'green' : domain.domain_type === 'UGC' ? 'purple' : 'slate'}>{domain.domain_type}</Badge></Td>
                  <Td>{domain.retrievals.toLocaleString()}</Td>
                  <Td className="font-semibold">{domain.retrieved.toFixed(1)}%</Td>
                  <Td>{domain.retrieval_rate.toFixed(1)}</Td>
                  <Td>{domain.citation_rate.toFixed(1)}</Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
          <div className="px-4 py-3 text-sm text-zinc-500">{filteredDomains.length} {t('pages.ofItems')} 289</div>
        </SectionCard>
      )}
    </PageShell>
  );
};

export default DomainsPage;
